import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { salesGet } from '@/lib/salesTeam';
import jobs from '../partner-jobs/PartnerJobs.module.css';

/**
 * The sales kit (Warren, 21 October 2026): a spec sheet for each charger and a one-page overview of the network, each
 * with the partner's name, licence, logo and contact. No prices and no earnings figures, by design.
 */
interface Kit { products: Array<{ id: string; name: string; part_number: string; tier: string; specs: Array<[string, string]> }> }

async function download(path: string, name: string) {
  const blob = await api.get(path).blob();
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
}

export function SalesKitPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const q = useQuery({ queryKey: ['partner-sales-kit'], queryFn: () => salesGet<Kit>('admin/partner-sales-kit') });
  const [message, setMessage] = useState('');
  const get = (path: string, name: string) => { setMessage(''); void download(path, name).catch(() => setMessage('The PDF did not download. Try again.')); };
  return <main className={jobs.page}>
    <div className={jobs.heading}><div><h1 className={jobs.title}>Sales kit</h1>
      <p className={jobs.lead}>Leave-behinds with your name on them: a spec sheet for each charger, and the EV Initiative network on one page. Your logo and licence come from <a href="/sales-quotes" onClick={e => { e.preventDefault(); onNavigate('/sales-quotes'); }}>Your business on quotes</a>.</p></div></div>
    {q.error && <p role="alert">{q.error.message}</p>}
    <section className={jobs.card}>
      <div className={jobs.cardHead}><h2 className={jobs.kicker}>The network on one page</h2>
        <button type="button" className={jobs.button} onClick={() => get('admin/partner-sales-kit/network', 'ev-initiative-network.pdf')}>Download PDF</button></div>
      <p className={jobs.hint}>What the site gets: chargers that arrive programmed, drivers who find and pay in the app, the owner's console, monitoring and support. No figures.</p>
    </section>
    {q.data && <section className={jobs.card}><h2 className={jobs.kicker}>Charger spec sheets</h2>
      {q.data.products.map(p => <div key={p.id} className={jobs.slot}>
        <div><div className={jobs.strong}>{p.name}</div><div className={jobs.sub}>{p.specs.filter(([k]) => k !== 'Network').map(([k, v]) => `${k} ${v}`).join(' · ') || p.part_number}</div></div>
        <button type="button" className={jobs.ghost} onClick={() => get(`admin/partner-sales-kit/spec-sheet/${p.id}`, `${p.part_number || 'charger'}-spec-sheet.pdf`)}>Spec sheet</button>
      </div>)}
    </section>}
    <p className={jobs.message} role="status">{message}</p>
  </main>;
}
